// Profil-Overlays (design_handoff_profil §5–§6): Login-Historie und Bild anpassen. Vollflächig mit Blur, Sheet Radius 32;
// solange offen scrollt der Hintergrund nicht. Daten und Handler kommen unverändert aus ProfileView.
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Cropper, { type Area } from 'react-easy-crop';
import {
  CheckCircle, CircleNotch, ClockCounterClockwise, Crop, DeviceMobile, GlobeSimple, Laptop, LockSimple, MagnifyingGlassMinus, MagnifyingGlassPlus, X,
} from '@phosphor-icons/react';
import { useDialogFokus } from '../../../../components/immobilien/ui/fokus';
import { geraetArt } from '../../../../lib/profil/profil';

export interface LoginEvent { id: string; created_at: string; browser: string; os: string; user_agent: string }

/** Sperrt das Scrollen der Seite, solange das Overlay offen ist. */
function useScrollSperre() {
  useEffect(() => {
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = vorher; };
  }, []);
}

function Overlay({ titel, untertitel, icon, breite, hoehe, onSchliessen, schliessenTip, hintergrundSchliesst, children, trennlinie = false }: {
  titel: string; untertitel: string; icon: ReactNode; breite: number; hoehe?: string; onSchliessen: () => void; schliessenTip: string;
  hintergrundSchliesst: boolean; children: ReactNode; trennlinie?: boolean;
}) {
  useScrollSperre();
  const sheet = useDialogFokus<HTMLDivElement>();
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onSchliessen(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onSchliessen]);
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 450, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', fontFamily: "'Manrope', -apple-system, sans-serif" }}
      onMouseDown={(e) => { if (hintergrundSchliesst && e.target === e.currentTarget) onSchliessen(); }}>
      <div ref={sheet} tabIndex={-1} role="dialog" aria-modal="true" aria-label={titel} className="immo-side-in flex w-full flex-col overflow-hidden outline-none"
        style={{ maxWidth: breite, height: hoehe, maxHeight: '100%', borderRadius: 32, background: 'var(--sheet)', color: 'var(--ink)',
          boxShadow: '0 40px 80px -30px rgba(0,0,0,0.5), inset 0 0 0 1px var(--line)' }}>
        <div className="flex shrink-0 items-center" style={{ gap: 12, padding: '18px 18px 14px 22px', borderBottom: trennlinie ? '1px solid var(--line)' : 'none' }}>
          {icon}
          <div className="flex min-w-0 flex-1 flex-col">
            <h2 className="m-0" style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}>{titel}</h2>
            <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{untertitel}</span>
          </div>
          <button type="button" onClick={onSchliessen} aria-label="Schließen" data-tip={schliessenTip}
            className="flex shrink-0 items-center justify-center" style={{ width: 44, height: 44, border: 'none', borderRadius: '50%', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink2)' }}>
            <X size={15} weight="bold" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

const kachel = (flaeche: string, farbe: string) => ({ width: 38, height: 38, borderRadius: 12, background: flaeche, color: farbe, boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.35)' });

export function LoginHistorieOverlay({ laedt, eintraege, mobil, onSchliessen }: { laedt: boolean; eintraege: LoginEvent[]; mobil: boolean; onSchliessen: () => void }) {
  return (
    <Overlay titel="Login-Historie" untertitel="Letzte 20 Aktivitäten" breite={620} hoehe={mobil ? '86vh' : '70vh'} onSchliessen={onSchliessen} schliessenTip="Schließen"
      hintergrundSchliesst trennlinie
      icon={<span className="flex shrink-0 items-center justify-center" style={kachel('linear-gradient(160deg, color-mix(in srgb, var(--warn) 65%, #fff), var(--warn))', '#fff')}><ClockCounterClockwise size={19} weight="fill" /></span>}>
      <div className="min-h-0 flex-1 overflow-auto" style={{ padding: 14 }}>
        {laedt ? (
          <div className="flex h-full flex-col items-center justify-center" style={{ gap: 12, color: 'var(--ink3)' }}>
            <CircleNotch size={30} weight="bold" className="animate-spin" />
            <span style={{ fontSize: 13.5, fontWeight: 700 }}>Sichere Verbindung wird aufgebaut …</span>
          </div>
        ) : eintraege.length > 0 ? (
          <div className="flex flex-col" style={{ borderRadius: 20, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
            {eintraege.map((item, i) => {
              const Geraet = geraetArt(item.os) === 'laptop' ? Laptop : DeviceMobile;
              return (
                <div key={item.id} className="flex items-center" style={{ gap: 12, padding: '12px 14px', borderTop: i ? '1px solid var(--line)' : 'none' }}>
                  <span className="flex shrink-0 items-center justify-center" style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--fill2)', color: 'var(--ink2)' }}>
                    <Geraet size={18} weight="bold" />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
                    <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{item.browser} auf {item.os}</span>
                    <span className="flex items-center tabular-nums" style={{ gap: 5, fontSize: 12.5, color: 'var(--ink3)' }}>
                      <GlobeSimple size={12} weight="bold" />{new Date(item.created_at).toLocaleString('de-DE')}
                    </span>
                  </div>
                  <span className="inline-flex shrink-0 items-center" style={{ height: 22, padding: '0 9px', gap: 5, borderRadius: 11, fontSize: 11.5, fontWeight: 800, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
                    <CheckCircle size={12} weight="fill" />Erfolgreich
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center text-center" style={{ gap: 10 }}>
            <span className="flex items-center justify-center" style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--fill2)', color: 'var(--ink3)' }}>
              <LockSimple size={24} weight="bold" />
            </span>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink2)' }}>Keine Login-Daten erfasst</span>
          </div>
        )}
      </div>
    </Overlay>
  );
}

export function BildAnpassenOverlay({ bild, crop, zoom, uploading, mobil, onCrop, onZoom, onCropComplete, onAbbrechen, onSpeichern }: {
  bild: string; crop: { x: number; y: number }; zoom: number; uploading: boolean; mobil: boolean;
  onCrop: (c: { x: number; y: number }) => void; onZoom: (z: number) => void; onCropComplete: (a: Area, p: Area) => void;
  onAbbrechen: () => void; onSpeichern: () => void;
}) {
  return (
    <Overlay titel="Bild anpassen" untertitel="Zuschnitt & Zoom" breite={520} onSchliessen={onAbbrechen} schliessenTip="Schließen – Ohne Speichern abbrechen"
      hintergrundSchliesst={false}
      icon={<span className="flex shrink-0 items-center justify-center" style={{ ...kachel('var(--acc-fill)', 'var(--acc-ink)'), boxShadow: 'var(--acc-sh)' }}><Crop size={19} weight="fill" /></span>}>
      <div data-tip="Verschieben – Bild ziehen, um den Ausschnitt festzulegen" className="relative shrink-0" style={{ height: mobil ? 300 : 340, background: '#0d0e11' }}>
        <Cropper image={bild} crop={crop} zoom={zoom} aspect={1} onCropChange={onCrop} onZoomChange={onZoom} onCropComplete={onCropComplete} cropShape="round" showGrid={false} />
      </div>
      <div className="flex flex-col" style={{ padding: '18px 22px 22px', gap: 18 }}>
        <div className="flex items-center" style={{ gap: 14 }}>
          <MagnifyingGlassMinus size={18} weight="bold" color="var(--ink3)" aria-hidden />
          <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => onZoom(parseFloat(e.target.value))}
            aria-label="Zoom" data-tip="Zoom – Von 1× bis 3×" className="immo-regler min-w-0 flex-1" />
          <MagnifyingGlassPlus size={18} weight="bold" color="var(--ink3)" aria-hidden />
        </div>
        <div className="flex" style={{ gap: 10 }}>
          <button type="button" onClick={onAbbrechen} data-tip="Abbrechen – Bild verwerfen"
            style={{ flex: 1, height: 48, border: 'none', borderRadius: 24, cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink)' }}>
            Abbrechen
          </button>
          <button type="button" onClick={onSpeichern} disabled={uploading} data-tip="Speichern – Ausschnitt als neues Profilbild hochladen"
            className="flex items-center justify-center"
            style={{ flex: 1.5, height: 48, gap: 8, border: 'none', borderRadius: 24, cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1,
              fontFamily: 'inherit', fontSize: 15, fontWeight: 800, background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
            {uploading ? <CircleNotch size={17} weight="bold" className="animate-spin" /> : <CheckCircle size={17} weight="bold" />}
            Speichern
          </button>
        </div>
      </div>
    </Overlay>
  );
}
